/* eslint-disable no-else-return */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from 'react';
import { useLocation, useNavigate, NavigateOptions } from 'react-router';

import { paths } from 'src/routes/paths';

import { isDev, getConfigBrandCode } from 'src/utils/configUtil/configUtil';

/**
 * 메인 위젯/딥링크/인박스에서 상세로 진입할 때 URL에 붙이는 쿼리 문자열.
 * - widget: 홈 위젯에서 상세로 이동 시 사용. 상세 화면에서 "목록으로 가기" 노출·뒤로가기 시 홈 이동 판단에 사용.
 * - deeplink: 딥링크에서 상세로 이동 시 사용. 동일하게 목록으로 가기·뒤로가기 동작에 사용.
 * - inbox: 인박스에서 상세로 이동 시 사용. "목록으로 가기" 노출·뒤로가기 시 인박스로 이동 판단에 사용.
 */
export const ENTRY_QUERY = {
  widget: 'entry=widget',
  deeplink: 'entry=deeplink',
  inbox: 'entry=inbox',
} as const;

/**
 * URL search에 위젯/딥링크/인박스 진입 쿼리(entry=widget | entry=deeplink | entry=inbox)가 있는지 여부.
 * 상세 화면에서 "목록으로 가기" UI 노출 여부 판단 시 사용.
 */
export function hasEntryParam(search: string): boolean {
  return (
    search.includes(ENTRY_QUERY.widget) ||
    search.includes(ENTRY_QUERY.deeplink) ||
    search.includes(ENTRY_QUERY.inbox)
  );
}

/**
 * 인박스에서 "목록으로 가기"를 노출하는 4개 상세 화면 path인지 여부.
 * 온라인 클래스 상세, 나의 클래스 상세, 커뮤니티 상세, 소셜러닝 상세로 이동할 때만 entry=inbox 부여에 사용.
 */
export function isInboxEntryTargetPath(link: string): boolean {
  const path = link.split('?')[0];
  // 온라인 클래스 상세: /degree/:id
  if (/\/degree\/[^/]+/.test(path)) return true;
  // 나의 클래스 상세: /learn/:learnId (한 세그먼트만, lesson/mission 등 하위 경로 제외)
  if (/\/learn\/[^/]+$/.test(path)) return true;
  // 커뮤니티 상세: /community/.../article/...
  if (path.includes('/community/') && path.includes('/article/')) return true;
  // 소셜러닝 상세: /social-learning/.../article/...
  if (path.includes('/social-learning/') && path.includes('/article/')) return true;
  return false;
}

type MoveToDetailOptions = {
  includeOriginalParams?: boolean;
  params?: { [key: string]: any };
  extraDepth?: string;
};

export type MoveToLearningContentType = {
  learnId: string;
  lessonId: string;
  progressionId: string;
  nextLesson: any;
};

/*
  useRoute hook 라우트 이동처리를 공통화
  화면이동
  - 기본 화면이동 : push 이동
  - 특수한 상황에서는 replace 처리

  뒤로가기
  - 기본 뒤로가기 : history back
  - history 없으면 메인화면으로 이동
*/

export function useRoute() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  // 2024-09-25 Patrick : Will be deleted
  const moveToDetail = useCallback((id: string, options?: MoveToDetailOptions) => {
    const combinedParams = new URLSearchParams(search);

    if (options?.params) {
      Object.entries(options?.params).forEach(([key, value]) => {
        combinedParams.set(key, String(value));
      });
    }

    const queryString = options?.includeOriginalParams ? `?${combinedParams.toString()}` : '';
    if (options?.extraDepth) {
      navigate(`${pathname}/${options.extraDepth}/${id}${queryString}`, {});
    } else {
      navigate(`${pathname}/${id}${queryString}`, {});
    }
  }, []);

  // 2024-09-25 Patrick : Will be deleted
  const moveToLearningContent = useCallback(
    ({ learnId, lessonId, progressionId }: MoveToLearningContentType) => {
      navigate(`/learn/${learnId}/lesson/${lessonId}/progression/${progressionId}`);
    },
    []
  );

  // 기본 화면 이동 (push)
  // replace가 필요하면 세 번째 인자 NavigateOptions로 전달: pushNavigate(path, search?, { replace: true })
  const pushNavigate = (path: string, searchParam?: string, state?: NavigateOptions) => {
    const companyCode = getConfigBrandCode();

    // path에서 쿼리 문자열 분리
    const [navigatePath, existingSearch] = path.split('?');
    // basename 중복 제거
    const normalizedPath = (() => {
      if (companyCode === '') {
        return navigatePath;
      }

      // localhost 환경에서는 첫 번째 설정을 기본으로 사용
      if (window.location.origin.includes('localhost') || process.env.NODE_ENV === 'development') {
        return navigatePath;
      }

      if (isDev()) {
        return navigatePath.replace(`/${companyCode}`, '');
      }

      if (companyCode && navigatePath.startsWith(`/${companyCode}`)) {
        return navigatePath.replace(`/${companyCode}`, '');
      }

      return navigatePath;
    })();

    // 디버깅 로그 추가
    console.log('useRoute: pushNavigate', {
      originalPath: path,
      normalizedPath,
      companyCode,
      currentPath: pathname,
      searchParam,
      isDirectUrl: pathname === navigatePath, // 주소창 입력 여부
    });

    // searchParam 앞의 '?' 제거
    const cleanExistingSearch = existingSearch?.replace(/^\?/, '');
    const cleanSearchParam = searchParam?.replace(/^\?/, '');

    // 쿼리 문자열이 있는지 조건문으로 작성
    let searchParams = '';
    if (cleanExistingSearch && cleanSearchParam) {
      searchParams = `${cleanExistingSearch}&${cleanSearchParam}`;
    } else if (cleanExistingSearch) {
      searchParams = cleanExistingSearch;
    } else if (cleanSearchParam) {
      searchParams = cleanSearchParam;
    }

    const fullPath = searchParams ? `${normalizedPath}?${searchParams}` : normalizedPath;
    navigate(fullPath, { ...state });
  };

  /**
   * 홈 위젯에서 상세 화면으로 이동할 때 사용.
   * 내부에서 entry=widget 쿼리를 붙인 뒤 pushNavigate 호출하여, 상세 화면에서 진입 경로(위젯) 인지 및 목록으로 가기 노출에 활용.
   */
  const pushNavigateFromWidgetEntry = (
    path: string,
    searchParam?: string,
    state?: NavigateOptions
  ) => {
    const base = ENTRY_QUERY.widget;
    const extra = searchParam?.replace(/^\?/, '');
    const merged = extra ? `${base}&${extra}` : base;
    pushNavigate(path, merged, state);
  };

  /**
   * 인박스에서 상세 화면(온라인 클래스/나의 클래스/커뮤니티/소셜러닝 상세)으로 이동할 때 사용.
   * 내부에서 entry=inbox 쿼리를 붙여, 상세에서 "목록으로 가기" 노출·뒤로가기 시 인박스로 이동하도록 함.
   */
  const pushNavigateFromInboxEntry = (
    path: string,
    searchParam?: string,
    state?: NavigateOptions
  ) => {
    const base = ENTRY_QUERY.inbox;
    const extra = searchParam?.replace(/^\?/, '');
    const merged = extra ? `${base}&${extra}` : base;
    pushNavigate(path, merged, state);
  };

  // 기본 뒤로가기 (history back)
  const goBack = () => {
    if (window.history.length < 1) {
      return navigate('/home', { replace: true });
    }

    // 인박스에서 상세로 진입한 경우 뒤로가기 시 인박스로 이동
    if (search.includes(ENTRY_QUERY.inbox)) {
      return navigate(paths.inbox.root);
    }
    // 위젯/딥링크에서 상세로 진입한 경우 뒤로가기 시 홈으로 이동 (목록이 히스토리에 없음)
    if (hasEntryParam(search)) {
      return navigate('/home');
    }
    return navigate(-1);
  };

  return {
    moveToDetail,
    moveToLearningContent,
    pushNavigate,
    pushNavigateFromWidgetEntry,
    pushNavigateFromInboxEntry,
    goBack,
  };
}
